import classNames from 'classnames';
import * as React from 'react';

const DEFAULT_STATE = {
  failed: false,
  loading: false,
  entries: []
};

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.BEGIN_LOADING:
      return { ...DEFAULT_STATE, loading: true };

    case actionTypes.LOAD_SUCCESS:
      return {
        failed: false,
        loading: false,
        entries: action.entries
      }

    case actionTypes.LOAD_FAILED:
      return {
        failed: true,
        loading: false,
        entries: []
      }
  }
  return state;
};

const actionTypes = {
  BEGIN_LOADING: 'beginLoading',
  LOAD_SUCCESS: 'loadSuccess',
  LOAD_FAILED: 'loadFailed'
};

const iconFor = (entry) => {
  if (entry.type === 'SystemNotificationStateTransition') {
    return 'fa-check';
  }

  if (entry.category?.icon) {
    if (entry.category.icon.startsWith('fa-')) {
      return entry.category.icon;
    }
    return `fa-${entry.category.icon}`;
  }
  switch(entry.type) {
    case 'Case':
      return 'fa-exclamation';
    case 'CaseStateTransition':
      if (entry.details.startsWith('Resolved:')) {
        return 'fa-check';
      }
      return 'fa-exclamation';
  }
}

const colourFor = (entry) => {
  if (entry.type === 'SupportRequestResolution') {
    return 'var(--success)';
  }

  if (entry.category?.colour) {
    return entry.category.colour;
  }

  if (entry.type === 'CaseStateTransition' && entry.details.startsWith('Resolved:')) {
    return 'var(--success)';
  }

  if (entry.type === 'SystemNotificationStateTransition') {
    return 'var(--success)';
  }

  return '#ff7401';
}

const CircleInner = ({ entry }) => {

  const classnames = classNames(
    'fa fa-fw',
    iconFor(entry)
  )

  return (
    <i
      className={classnames}
      aria-hidden="true"
    ></i>);
}

const titleFor = (entry) => {
  if (entry.type === 'SupportRequest') {
    return 'Support request created';
  }

  if (entry.type === 'SupportRequestResolution') {
    return 'Support request resolved';
  }

  if (entry.category?.name) {
    switch (entry.type) {
      case 'SystemNotification':
        return `System notification: ${entry.category.name}`;
      case 'SystemNotificationStateTransition':
        return `System notification acknowledged: ${entry.category.name}`;
      case 'EnvironmentLog':
        return `Changelog entry: ${entry.category.name}`;
      default:
        return entry.category.name;
    }
  }

  switch (entry.type) {
    case 'Case':
      return 'Case opened'
    case 'CaseStateTransition':
      if (entry.details.startsWith('Resolved:')) {
        return 'Case resolved'
      }
      if (entry.details.startsWith('Reopened:')) {
        return 'Case reopened'
      }
      return 'Case updated'
    default:
      return entry.type
  }
}

const ENTRY_TYPE_TRANSLATION = {
  'CaseStateTransition': 'Case',
  'SystemNotificationStateTransition': 'System notification'
}

const Circle = ({ entry }) => {

  const title = titleFor(entry);
  const colour = colourFor(entry);

  return (
    <div
      className="circle"
      title={title}
      style={{ backgroundColor: colour }}
    >
      {
        entry.link && (
          <a href={entry.link}>
            <CircleInner entry={entry} />
          </a>
        )
      }
      {
        !entry.link && (
          <CircleInner entry={entry} />
        )
      }
    </div>
  );
}

export const RecentActivity = ({ compact, componentId, asset, activityURL, envopsMode, hideHeader }) => {
  const [{ failed, loading, entries }, dispatch] = React.useReducer(reducer, DEFAULT_STATE);

  React.useEffect(
    () => {
      dispatch({ type: actionTypes.BEGIN_LOADING });

      const controller = new AbortController();

      let url = activityURL;
      if (asset) {
        url += `?for=${asset.type}-${asset.id}`;
      }

      fetch(url, {
        credentials: "include",
        headers: {
          "Accept": "application/json",
        },
        signal: controller.signal
      }).then((response) => {
        response.json().then(
          (entries) => {
            dispatch({ type: actionTypes.LOAD_SUCCESS, entries });
            if (!compact) {
              window.dispatchEvent(
                new CustomEvent('recentActivity:loaded')
              );
            }
          }
        );
      }).catch(
        (e) => {
          if (e.name !== 'AbortError') {
            dispatch({ type: actionTypes.LOAD_FAILED });
          }
        }
      );

      return () => {
        controller.abort("Request was cancelled");
      }

    },
    [asset, activityURL]
  );

  let header = <h6>Recent activity</h6>

  if (envopsMode) {
    let addEntryParams = ""

    if (asset) {
      if (asset.type === "Chassis") {
        addEntryParams = asset.assets.map(a => `${asset.typeParam}s[]=${a.id}`).join("&")
      } else {
        addEntryParams = `${asset.typeParam}s[]=${asset.id}`
      }
    }
    header = (
      <div className="d-flex flex-row justify-content-between align-items-center">
        <h6>Recent activity</h6>
        <a
          href={`/components/${componentId}/environment_logs/new?${addEntryParams}`}
          className=""
        >
          + Add event
        </a>
      </div>
    )
  }

  if (loading) {
    return (
      <>
        {!hideHeader && header}
        <em className="smallish text-muted">Loading...</em>
      </>
    )
  }

  if (failed) {
    return (
      <>
        {!hideHeader && header}
        <em className="smallish text-muted">Failed to retrieve recent activity</em>
      </>
    )
  }

  if (entries.length === 0) {
    return (
      <>
        {!hideHeader && header}
        <em className="smallish text-muted">No recent activity</em>
      </>
    )
  }

  const footerClasses = classNames(
    'text-muted',
    {
      'd-flex justify-content-between flex-wrap': !compact,
      'smallish': compact
    }
  );

  return (
    <>
      {!hideHeader && header}
      <ul className="recent-activity mt-2">
        {entries.map((entry) =>
          <li
            key={`entry-${entry.type}-${entry.id}`}
          >
            <Circle entry={entry} />
            <div
              className={classNames('content association-target', { 'with-associations': entry.assets.length > 0 })}
              data-assets={JSON.stringify(entry.assets)}
            >
              <div
                className={compact ? "smallish" : undefined}
                dangerouslySetInnerHTML={{ __html: entry.details }}
              />
              <div className={footerClasses}>
                <p className="associations mb-0">
                  <i
                    className="fa fa-cubes mr-2 asset-icon"
                    title="This entry has associated assets"
                  ></i>
                  {entry.associations}
                </p>
                <p className="mb-0">
                  { entry.createdAt }
                </p>
              </div>
            </div>
          </li>
        )}
      </ul>
    </>
  )
}
